import { useMemo } from 'react';
import { Activity, Download, Loader2, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useKappaMetrics } from '@/features/review/hooks/useReview';
import { irrToCSV } from '@/features/review/irr';
import type { ReviewPhase } from '@/features/review/types';

interface IRRMetricsProps {
  reviewId: string;
  phase?: ReviewPhase;
  className?: string;
}

const KAPPA_COLORS: Record<string, string> = {
  'Almost Perfect': 'bg-emerald-100 text-emerald-700',
  Substantial: 'bg-emerald-50 text-emerald-600',
  Moderate: 'bg-amber-100 text-amber-700',
  Fair: 'bg-amber-50 text-amber-600',
  Slight: 'bg-orange-100 text-orange-700',
  Poor: 'bg-rose-100 text-rose-700',
  'No shared decisions': 'bg-slate-100 text-slate-500',
};

export function IRRMetrics({ reviewId, phase, className }: IRRMetricsProps) {
  const { data: pairs, isLoading } = useKappaMetrics(reviewId, phase);

  // Global average agreement
  const globalAgreement = useMemo(() => {
    if (!pairs || pairs.length === 0) return null;
    const total = pairs.reduce((sum, p) => sum + p.observed_agreement * p.n, 0);
    const totalN = pairs.reduce((sum, p) => sum + p.n, 0);
    return totalN > 0 ? total / totalN : 0;
  }, [pairs]);

  const handleExportCSV = () => {
    if (!pairs) return;
    const csv = irrToCSV(
      pairs.map((p) => ({
        reviewer1: p.reviewer1_name,
        reviewer2: p.reviewer2_name,
        n: p.n,
        agreement: p.observed_agreement,
        kappa: p.kappa,
        interpretation: p.interpretation,
      })),
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'irr-metrics.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!pairs || pairs.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Users className="h-10 w-10 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-600">
            No inter-rater data available
          </p>
          <p className="text-xs text-slate-400 mt-1">
            At least 2 reviewers need to screen the same sources
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-indigo-500" />
              <CardTitle className="text-base">Inter-Rater Reliability</CardTitle>
            </div>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleExportCSV}>
              <Download className="h-3.5 w-3.5 mr-1" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {globalAgreement != null && (
            <div className="flex items-center gap-3 mb-4">
              <div className="text-2xl font-bold text-slate-900">
                {Math.round(globalAgreement * 100)}%
              </div>
              <div>
                <p className="text-sm text-slate-600">Global Agreement</p>
                <p className="text-xs text-slate-400">
                  Across {pairs.length} reviewer pair{pairs.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          )}

          {/* Pairs table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-3 text-xs font-medium text-slate-500">
                    Reviewer A
                  </th>
                  <th className="text-left py-2 pr-3 text-xs font-medium text-slate-500">
                    Reviewer B
                  </th>
                  <th className="text-center py-2 pr-3 text-xs font-medium text-slate-500">
                    N
                  </th>
                  <th className="text-center py-2 pr-3 text-xs font-medium text-slate-500">
                    % Agreement
                  </th>
                  <th className="text-center py-2 pr-3 text-xs font-medium text-slate-500">
                    Kappa
                  </th>
                  <th className="text-left py-2 text-xs font-medium text-slate-500">
                    Interpretation
                  </th>
                </tr>
              </thead>
              <tbody>
                {pairs.map((pair, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="py-2 pr-3 text-slate-700">{pair.reviewer1_name}</td>
                    <td className="py-2 pr-3 text-slate-700">{pair.reviewer2_name}</td>
                    <td className="py-2 pr-3 text-center text-slate-600">{pair.n}</td>
                    <td className="py-2 pr-3 text-center text-slate-600">
                      {Math.round(pair.observed_agreement * 100)}%
                    </td>
                    <td className="py-2 pr-3 text-center font-mono text-slate-700">
                      {pair.kappa != null ? pair.kappa.toFixed(3) : '—'}
                    </td>
                    <td className="py-2">
                      <Badge
                        variant="secondary"
                        className={cn(
                          'text-[10px]',
                          KAPPA_COLORS[pair.interpretation] ?? 'bg-slate-100 text-slate-500',
                        )}
                      >
                        {pair.interpretation}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
